import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl, timeAgo } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Avatar, Empty, PageHeader, Spinner } from "@/components/common";

const Thread = ({ cid, me, onSent }) => {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const end = useRef();
  useEffect(() => {
    const load = () => api.get(`/messages/${cid}`).then((r) => setData(r.data)).catch(() => setData(false));
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [cid]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [data?.messages?.length]);
  if (data === false) return <Empty title="Conversation not found" />;
  if (!data) return <Spinner />;
  const send = async () => {
    if (!text.trim()) return;
    try { const { data: m } = await api.post(`/messages/${cid}`, { text }); setData({ ...data, messages: [...data.messages, m] }); setText(""); onSent(); } catch (e) { toast.error(errMsg(e)); }
  };
  const c = data.conversation;
  return (
    <div className="flex h-[calc(100dvh-16rem)] min-h-[360px] flex-col md:h-[calc(100dvh-19rem)]" data-testid="message-thread">
      <div className="flex items-center gap-3 border-b border-border p-4">
        <Link to="/messages" className="md:hidden"><ArrowLeft size={18} /></Link>
        <Avatar user={c.other} size={36} />
        <div className="min-w-0 flex-1"><p className="font-medium">{c.other?.name}</p></div>
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto p-4">
        {data.messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender_id === me.user_id ? "justify-end" : ""}`}>
            <div className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm ${m.sender_id === me.user_id ? "bg-slate-900 text-white" : "bg-[#F5F1EB] text-slate-800"}`} data-testid="message-bubble">
              {m.text}<p className={`mt-0.5 text-[10px] ${m.sender_id === me.user_id ? "text-slate-400" : "text-slate-500"}`}>{timeAgo(m.created_at)}</p>
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <div className="flex gap-2 border-t border-border p-3">
        <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Write a message…"
          className="flex-1 rounded-full border border-border px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-slate-200" />
        <button data-testid="message-send-btn" onClick={send} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-white" aria-label="Send"><Send size={16} /></button>
      </div>
    </div>
  );
};

export default function Messages() {
  const { convId } = useParams();
  const { me } = useMe();
  const [convs, setConvs] = useState(null);
  const load = () => api.get("/messages").then((r) => setConvs(r.data));
  useEffect(() => { load(); }, [convId]);

  return (
    <div data-testid="messages-page">
      <PageHeader title="Messages" subtitle="Private conversations with your connections." />
      <div className="surface grid overflow-hidden md:grid-cols-[320px_1fr]">
        <div className={`border-r border-border ${convId ? "hidden md:block" : ""}`}>
          {convs === null ? <Spinner /> : convs.length === 0 ? <p className="p-6 text-sm text-slate-500">No conversations yet. Message someone from their profile.</p> :
            convs.map((c) => (
              <Link key={c.id} to={`/messages/${c.id}`} data-testid={`conversation-${c.id}`} className={`flex items-center gap-3 border-b border-border p-4 transition-colors hover:bg-[#FAF8F5] ${c.id === convId ? "bg-[#F5F1EB]" : ""}`}>
                <Avatar user={c.other} size={40} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2"><p className={`truncate text-sm ${c.unread ? "font-semibold" : "font-medium"}`}>{c.other?.name}</p><span className="shrink-0 text-[11px] text-slate-400">{timeAgo(c.updated_at)}</span></div>
                  <p className="truncate text-xs text-slate-500">{c.last_message}</p>
                </div>
                {c.unread && <span className="h-2 w-2 rounded-full bg-[hsl(var(--social))]" />}
              </Link>
            ))}
        </div>
        <div className={convId ? "" : "hidden md:block"}>
          {convId ? <Thread cid={convId} me={me} onSent={load} /> : <div className="flex h-80 items-center justify-center text-sm text-slate-400">Select a conversation</div>}
        </div>
      </div>
    </div>
  );
}
